
import { Row, Col } from "react-bootstrap";
import DataTable from "react-data-table-component";
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import React, { useEffect, useState } from 'react';
import axios from "axios";
import { right } from "@popperjs/core";
import { useApiClient } from "../../ApiService/useApiClient";

const CustomerIncidents = ({ CustomerID }) => {
  const api = useApiClient();

  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [error, setError] = useState();
  const [editMode, setEditMode] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const initialFormValue = {
    CustomerID: CustomerID,
    IncidentName: '',
    IncidentDetail: '',
    IncidentActions: '',
    IncidentReportedBy: '',
    IncidentStatus: 'Open',
    IncidentDate: '',
  };

  const [formValue, setFormValue] = useState(initialFormValue);

  const validateForm = () => {
    const errors = {};
    if (!formValue.IncidentName.trim()) errors.IncidentName = 'Incident Name is required.';
    if (!formValue.IncidentDetail.trim()) errors.IncidentDetail = 'Incident Detail is required.';
    if (!formValue.IncidentDate) errors.IncidentDate = 'Incident Date is required.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tableColumns = [
    {
      name: "Incident Name",
      selector: (row) => row.incidentName,
      sortable: true,
    },
    {
      name: "Incident Date",
      selector: (row) =>
        row.incidentDate ? new Date(row.incidentDate).toLocaleDateString("en-GB") : "",
      sortable: true,
    },
    {
      name: "Incident Status",
      selector: (row) => row.incidentStatus,
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div>
          <Button variant="primary" size="sm" onClick={() => handleEditIncident(row)}>Edit</Button>{" "}
          <Button variant="danger" size="sm" onClick={() => handleDeleteIncident(row.id)}>Delete</Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const CompleteURL = "CustomerIncident/incidentlist/";
      try {
        const response = await api.get(CompleteURL + CustomerID + "/");
        setData(response.data);
      } catch (error) {
        setError(error);
      }
    };

    fetchData();
  }, []);

  const handleClose = () => {
    setEditMode(false);
    setEditingIncident(null);
    setFormValue(initialFormValue); // reset form
    setFormErrors({});
    setShow(false);
  };

  const handleShow = () => {
    setEditMode(false);
    setEditingIncident(null);
    setFormValue(initialFormValue); // ensure clean state on "Add"
    setFormErrors({});
    setShow(true);
  };

  const handleChange = (event) => {
    setFormValue({
      ...formValue,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const frmFormData = new FormData();
    for (const key in formValue) {
      frmFormData.append(key, formValue[key]);
    }

    const sURL = "CustomerIncident/incidentlist/add/";

    await api.post(sURL, frmFormData)
      .then((Response) => {
        setData([...data, Response.data]);
      })
      .catch((error) => {
        setError(error);
      });

    handleClose();
  };

  const handleEditIncident = (incident) => {
    setEditingIncident(incident);
    setFormValue({
      CustomerID: CustomerID,
      IncidentName: incident.incidentName,
      IncidentDetail: incident.incidentDetail,
      IncidentActions: incident.incidentActions,
      IncidentReportedBy: incident.incidentReportedBy,
      IncidentStatus: incident.incidentStatus,
      IncidentDate: incident.incidentDate ? incident.incidentDate.split("T")[0] : '',
    });
    setEditMode(true);
    setFormErrors({});
    setShow(true);
  };

  const handleUpdateIncident = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const frmFormData = new FormData();
    frmFormData.append("Id", editingIncident.id);
    for (const key in formValue) {
      frmFormData.append(key, formValue[key]);
    }

    const sURL = "CustomerIncident/incidentlist/update/" + editingIncident.id;

    api.put(sURL, frmFormData)
      .then((Response) => {
        const updatedData = data.map((incident) =>
          incident.id === Response.data.id ? { ...incident, ...Response.data } : incident
        );
        setData(updatedData);
      })
      .catch((error) => {
        setError(error);
      });

    handleClose();
  };

  const handleDeleteIncident = async (id) => {
    const sURL = `CustomerIncident/incidentlist/delete/${id}`;
    if (window.confirm("Are you sure you want to delete this incident?")) {
      try {
        await api.delete(sURL);
        const updatedData = data.filter((incident) => incident.id !== id);
        setData(updatedData);
      } catch (error) {
        setError(error);
      }
    }
  };

  return (
    <>
      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <Button variant="outline-primary" onClick={handleShow} size="sm">
            + Add an Incident
          </Button>
        </Col>
      </Row>

      <Row>
        {data && (
          <DataTable
            tableClassName="data-table-head-light table-responsive"
            data={data}
            columns={tableColumns}
            striped
            highlightOnHover
            pagination
            key="Id"
          />
        )}
      </Row>

      {show && (
        <Modal show={show} onHide={handleClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Incident' : 'Add Incident'}</Modal.Title>
          </Modal.Header>

          <Form onSubmit={editMode ? handleUpdateIncident : handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3" controlId="IncidentName">
                <Form.Label>Incident Name</Form.Label>
                <Form.Control
                  type="text"
                  name="IncidentName"
                  value={formValue.IncidentName}
                  onChange={handleChange}
                  isInvalid={!!formErrors.IncidentName}
                />
                <Form.Control.Feedback type="invalid">{formErrors.IncidentName}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="IncidentDetail">
                <Form.Label>Incident Detail</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="IncidentDetail"
                  value={formValue.IncidentDetail}
                  onChange={handleChange}
                  isInvalid={!!formErrors.IncidentDetail}
                />
                <Form.Control.Feedback type="invalid">{formErrors.IncidentDetail}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="IncidentActions">
                <Form.Label>Incident Actions</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="IncidentActions"
                  value={formValue.IncidentActions}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="IncidentReportedBy">
                <Form.Label>Reported By</Form.Label>
                <Form.Control
                  type="text"
                  name="IncidentReportedBy"
                  value={formValue.IncidentReportedBy}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="IncidentStatus">
                <Form.Label>Incident Status</Form.Label>
                <Form.Select
                  name="IncidentStatus"
                  value={formValue.IncidentStatus}
                  onChange={handleChange}
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="IncidentDate">
                <Form.Label>Incident Date</Form.Label>
                <Form.Control
                  type="date"
                  name="IncidentDate"
                  value={formValue.IncidentDate}
                  onChange={handleChange}
                  isInvalid={!!formErrors.IncidentDate}
                />
                <Form.Control.Feedback type="invalid">{formErrors.IncidentDate}</Form.Control.Feedback>
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="danger" onClick={handleClose}>Cancel</Button>
              <Button variant="primary" type="submit">
                {editMode ? 'Update Incident' : 'Add Incident'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default CustomerIncidents;



// import { Row, Col } from "react-bootstrap";
// import DataTable from "react-data-table-component";
// import Form from 'react-bootstrap/Form';
// import Modal from 'react-bootstrap/Modal';
// import Button from 'react-bootstrap/Button';
// import React, { useEffect, useState } from 'react';
// import axios from "axios";
// import { right } from "@popperjs/core";
// import { useApiClient } from "../../ApiService/useApiClient";


// const CustomerIncidents = ({ CustomerID }) => {

//   const api = useApiClient();

//   const [data, setData] = useState(null);
//   const [show, setShow] = useState(false);
//   const [error, setError] = useState();
//   const [editMode, setEditMode] = useState(false);
//   const [editingIncident, setEditingIncident] = useState(null);

//   const tableColumns = [
//     {
//       name: "Incident Name",
//       selector: (row) => row.incidentName,
//       sortable: true,
//     },
//     {
//       name: "Incident Date",
//       selector: (row) =>
//         row.incidentDate
//           ? new Date(row.incidentDate).toLocaleDateString("en-GB")
//           : "",
//       sortable: true,
//     },
//     {
//       name: "Incident Status",
//       selector: (row) => row.incidentStatus,
//       sortable: true,
//     },
//     {
//       name: "Actions",
//       selector: (row) => (
//         <div>
//           <Button variant="primary" size="sm" onClick={() => handleEditIncident(row)}>
//             Edit
//           </Button>{" "}
//           <Button variant="danger" size="sm" onClick={() => handleDeleteIncident(row.id)}>
//             Delete
//           </Button>
//         </div>
//       ),
//       ignoreRowClick: true,
//       allowOverflow: true,
//       button: true,
//     },
//   ];

//   useEffect(() => {
//     const fetchData = async () => {
//       const CompleteURL = "CustomerIncident/incidentlist/";

//       try {
//         const response = await api.get(CompleteURL + CustomerID + "/");
//         setData(response.data);
//       } catch (error) {
//         setError(error);
//       }
//     };

//     fetchData();
//   }, []);

//   const handleClose = () => {
//     setEditMode(false);
//     setEditingIncident(null);
//     setShow(false);
//   };

//   const handleShow = () => setShow(true);

//   const [formValue, setFormValue] = useState({
//     CustomerID: CustomerID,
//     IncidentName: '',
//     IncidentDetail: '',
//     IncidentActions: '',
//     IncidentReportedBy: '',
//     IncidentStatus: 'Open',
//     IncidentDate: '', // new field
//   });

//   const handleChange = (event) => {
//     setFormValue({
//       ...formValue,
//       [event.target.name]: event.target.value,
//     });
//   };

//   const handleSubmit = async (event) => {
//     event.preventDefault();

//     const frmFormData = new FormData();
//     frmFormData.append("CustomerID", CustomerID);
//     frmFormData.append("IncidentName", formValue.IncidentName);
//     frmFormData.append("IncidentDetail", formValue.IncidentDetail);
//     frmFormData.append("IncidentActions", formValue.IncidentActions);
//     frmFormData.append("IncidentReportedBy", formValue.IncidentReportedBy);
//     frmFormData.append("IncidentStatus", formValue.IncidentStatus);
//     frmFormData.append("IncidentDate", formValue.IncidentDate); // new field

//     const sURL = "CustomerIncident/incidentlist/add/";

//     await api.post(sURL, frmFormData)
//       .then((Response) => {
//         setData([...data, Response.data]);
//         console.log(Response.data);
//       })
//       .catch((error) => {
//         setError(error);
//       });

//     handleClose();
//   };

//   const handleEditIncident = (incident) => {
//     setEditingIncident(incident);
//     setFormValue({
//       CustomerID: CustomerID,
//       IncidentName: incident.incidentName,
//       IncidentDetail: incident.incidentDetail,
//       IncidentActions: incident.incidentActions,
//       IncidentReportedBy: incident.incidentReportedBy,
//       IncidentStatus: incident.incidentStatus,
//       IncidentDate: incident.incidentDate ? incident.incidentDate.split("T")[0] : '', // new
//     });
//     setEditMode(true);
//     setShow(true);
//   };

//   const handleUpdateIncident = async (event) => {
//     event.preventDefault();

//     const frmFormData = new FormData();
//     frmFormData.append("Id", editingIncident.id);
//     frmFormData.append("CustomerID", CustomerID);
//     frmFormData.append("IncidentName", formValue.IncidentName);
//     frmFormData.append("IncidentDetail", formValue.IncidentDetail);
//     frmFormData.append("IncidentActions", formValue.IncidentActions);
//     frmFormData.append("IncidentReportedBy", formValue.IncidentReportedBy);
//     frmFormData.append("IncidentStatus", formValue.IncidentStatus);
//     frmFormData.append("IncidentDate", formValue.IncidentDate); // new field

//     const sURL = "CustomerIncident/incidentlist/update/" + editingIncident.id;

//     api.put(sURL, frmFormData)
//       .then((Response) => {
//         const updatedData = data.map((incident) =>
//           incident.id === Response.data.id ? { ...incident, ...Response.data } : incident
//         );
//         setData(updatedData);
//         console.log(Response.data);
//       })
//       .catch((error) => {
//         setError(error);
//       });

//     handleClose();
//   };

//   const handleDeleteIncident = async (id) => {
//     const sURL = `CustomerIncident/incidentlist/delete/${id}`;

//     if (window.confirm("Are you sure you want to delete this incident?")) {
//       try {
//         await api.delete(sURL);
//         const updatedData = data.filter((incident) => incident.id !== id);
//         setData(updatedData);
//         console.log(`Incident with ID ${id} deleted successfully.`);
//       } catch (error) {
//         console.error("Error deleting incident:", error);
//         setError(error);
//       }
//     }
//   };

//   return (
//     <>
//       <Row className="mb-3">
//         <Col className="d-flex justify-content-end">
//           <Button
//             variant="outline-primary"
//             onClick={handleShow}
//             size="sm"> + Add a Incident </Button>
//         </Col>
//       </Row>

//       <Row>
//         {data && (<DataTable
//           tableClassName="data-table-head-light table-responsive"
//           data={data}
//           columns={tableColumns}
//           striped={true}
//           highlightOnHover={true}
//           filterFieldName={"IncidentName"}
//           pagination={true}
//           key="Id"
//         />)}
//       </Row>

//       {show && (
//         <Modal show={show} onHide={handleClose} size="lg">
//           <Modal.Header closeButton>
//             <Modal.Title>{editMode ? 'Edit Incident' : 'Add Incident'}</Modal.Title>
//           </Modal.Header>

//           <Form onSubmit={editMode ? handleUpdateIncident : handleSubmit}>
//             <Modal.Body>
//               <Form.Group className="mb-3" controlId="IncidentName">
//                 <Form.Label>Incident Name</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="IncidentName"
//                   value={formValue.IncidentName}
//                   onChange={handleChange}
//                 />
//               </Form.Group>

//               <Form.Group className="mb-3" controlId="IncidentDetail">
//                 <Form.Label>Incident Detail</Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows={3}
//                   name="IncidentDetail"
//                   value={formValue.IncidentDetail}
//                   onChange={handleChange}
//                 />
//               </Form.Group>

//               <Form.Group className="mb-3" controlId="IncidentActions">
//                 <Form.Label>Incident Actions</Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows={3}
//                   name="IncidentActions"
//                   value={formValue.IncidentActions}
//                   onChange={handleChange}
//                 />
//               </Form.Group>

//               <Form.Group className="mb-3" controlId="IncidentReportedBy">
//                 <Form.Label>Reported By</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="IncidentReportedBy"
//                   value={formValue.IncidentReportedBy}
//                   onChange={handleChange}
//                 />
//               </Form.Group>

//               <Form.Group className="mb-3" controlId="IncidentStatus">
//                 <Form.Label>Incident Status</Form.Label>
//                 <Form.Select
//                   name="IncidentStatus"
//                   value={formValue.IncidentStatus}
//                   onChange={handleChange}
//                 >
//                   <option value="Open">Open</option>
//                   <option value="In Progress">In Progress</option>
//                   <option value="Resolved">Resolved</option>
//                   <option value="Closed">Closed</option>
//                 </Form.Select>
//               </Form.Group>

//               <Form.Group className="mb-3" controlId="IncidentDate">
//                 <Form.Label>Incident Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="IncidentDate"
//                   value={formValue.IncidentDate}
//                   onChange={handleChange}
//                 />
//               </Form.Group>
//             </Modal.Body>

//             <Modal.Footer>
//               <Button variant="danger" onClick={handleClose}>
//                 Cancel
//               </Button>
//               <Button variant="primary" type="submit">
//                 {editMode ? 'Update Incident' : 'Add Incident'}
//               </Button>
//             </Modal.Footer>
//           </Form>
//         </Modal>
//       )}
//     </>
//   );
// };

// export default CustomerIncidents;

